export type Category =
  | "closed"
  | "resolved"
  | "active"
  | "planned"
  | "other"
  | "removed";
export interface BoardItem {
  id: number;
  parentId: number | null;
  targetDate?: string | null;
  type: string;
  title: string;
  state: string;
  category: Category;
  owner: string;
  iteration: string;
  changedAt: string;
  stateChangedAt?: string | null;
  tags: string[];
  blocked: boolean;
  description: string;
  url: string;
  pullRequests: { label: string; url: string }[];
}
export interface Snapshot {
  project: string;
  asOf: string;
  fetchedAt: string;
  items: BoardItem[];
}
// Completion visibility follows the state transition, never an unrelated edit.
export function showEpicInPortfolio(epic: BoardItem, showAllCompleted: boolean, now: number): boolean {
  if (epic.category === "removed") return false;
  if (showAllCompleted || (epic.category !== "resolved" && epic.category !== "closed")) return true;
  const changed = Date.parse(epic.stateChangedAt ?? "");
  return Number.isFinite(changed) && changed <= now && now - changed < 86_400_000;
}
export const categories = [
  "closed",
  "resolved",
  "active",
  "planned",
  "other",
] as const;
export const labels: Record<Category, string> = {
  closed: "Closed",
  resolved: "Resolved",
  active: "In progress",
  planned: "Planned",
  other: "Other",
  removed: "Removed",
};
export function descendants(root: number, items: BoardItem[]): BoardItem[] {
  const children = new Map<number, BoardItem[]>();
  for (const item of items) {
    if (item.parentId !== null)
      children.set(item.parentId, [
        ...(children.get(item.parentId) ?? []),
        item,
      ]);
  }
  const seen = new Set([root]);
  const result: BoardItem[] = [];
  const queue = [root];
  for (let i = 0; i < queue.length; i++) {
    for (const child of children.get(queue[i]!) ?? []) {
      if (seen.has(child.id)) continue;
      seen.add(child.id);
      queue.push(child.id);
      if (child.category !== "removed") result.push(child);
    }
  }
  return result;
}
// Count a story/bug once, not again for each implementation task beneath it.
// Tasks directly under a feature/epic remain visible and count as scoped work.
export function deliveryItems(items: BoardItem[]): BoardItem[] {
  const byId = new Map(items.map((item) => [item.id, item]));
  return items.filter((item) => {
    if (["Epic", "Feature"].includes(item.type) || item.category === "removed")
      return false;
    if (item.type !== "Task") return true;
    let parent = item.parentId;
    const seen = new Set<number>();
    while (parent !== null && !seen.has(parent)) {
      seen.add(parent);
      const ancestor = byId.get(parent);
      if (!ancestor) break;
      if (!["Epic", "Feature", "Task"].includes(ancestor.type)) return false;
      parent = ancestor.parentId;
    }
    return true;
  });
}
export function counts(items: BoardItem[]) {
  const result = {
    closed: 0,
    resolved: 0,
    active: 0,
    planned: 0,
    other: 0,
    total: 0,
    // Blockers are operational signals, including child tasks and features.
    // They must not disappear when we de-duplicate delivery counts.
    blocked: items.filter((item) => item.blocked && item.category !== "removed")
      .length,
  };
  for (const item of deliveryItems(items)) {
    result[item.category === "removed" ? "other" : item.category]++;
    result.total++;
  }
  return { ...result, complete: result.closed + result.resolved };
}
export function percentage(n: number, total: number) {
  return total ? Math.round((n / total) * 100) : 0;
}

export function epicActivityRank(epic: BoardItem, children: BoardItem[]) {
  if (
    epic.category === "active" ||
    children.some((item) => item.category === "active")
  )
    return 0;
  if (epic.category === "resolved") return 1;
  if (epic.category === "planned") return 2;
  return 3;
}

export function featureMatches(
  feature: BoardItem,
  children: BoardItem[],
  search: string,
  category: string,
) {
  const textMatches =
    !search ||
    matches(feature, search) ||
    children.some((item) => matches(item, search));
  const stateMatches =
    category === "all" ||
    (category === "blocked"
      ? feature.blocked || children.some((item) => item.blocked)
      : children.some((item) => item.category === category));
  return textMatches && stateMatches;
}
export function boardHref(id?: number) {
  return id === undefined ? "/boards" : `/boards/epics/${id}`;
}
export function routeEpic(path: string) {
  const match = /^\/boards\/epics\/(\d+)\/?$/.exec(path);
  return match ? Number(match[1]) : undefined;
}
export function matches(item: BoardItem, search: string) {
  return `${item.id} ${item.title} ${item.owner} ${item.tags.join(" ")}`
    .toLowerCase()
    .includes(search.toLowerCase());
}

// A PR linked to several descendants is one piece of evidence, with all tickets retained.
export function epicPullRequests(items: BoardItem[]) {
  const links = new Map<string, { label: string; url: string; items: BoardItem[] }>();
  for (const item of items) for (const link of item.pullRequests) {
    const entry = links.get(link.url) ?? { ...link, items: [] };
    if (!entry.items.some((x) => x.id === item.id)) entry.items.push(item);
    links.set(link.url, entry);
  }
  return [...links.values()].sort((a, b) => b.label.localeCompare(a.label, undefined, { numeric: true }));
}
export function milestoneFeatures(items: BoardItem[]) {
  return items.filter((x) => x.type === "Feature" && x.category !== "removed")
    .sort((a, b) => (a.targetDate ?? "9999").localeCompare(b.targetDate ?? "9999") || a.id - b.id);
}

export function pipelineLabel(status: string, result: string) {
  if (status !== "completed") return ({ inProgress: "Running", notStarted: "Queued", cancelling: "Cancelling", postponed: "Postponed" } as Record<string, string>)[status] ?? "Unknown";
  return ({ succeeded: "Passed", partiallySucceeded: "Partially succeeded", failed: "Failed", canceled: "Cancelled" } as Record<string, string>)[result] ?? "Unknown";
}
